import React, { useEffect, useRef, useState } from "react";
import {
  FlatList,
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { emojiArr } from "../../utils/emoji";
import {
  black,
  white,
  color_main,
  ColorF5F5F5,
} from "../../assets/style/colors";
import { useWindowDimensions } from 'react-native';
import { isIos } from "../../AppEvent";
import { FileResponse } from "../../utils/upload";

export type EmojiPickerModalProps = {
  visible: boolean;
  onClose: () => void;
  onEmoji: (emoji: string) => void;
  onPicture?: (res: FileResponse) => void;
  iconPosition?: { x: number; y: number; width: number; height: number };
};

export const EmojiPickerModal = ({ visible, onClose, onEmoji, onPicture, iconPosition }: EmojiPickerModalProps) => {
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();

  const handleEmojiPress = (emoji: string) => {
    console.log('[EmojiPickerModal] handleEmojiPress 被调用, emoji:', emoji);
    onEmoji(emoji);
    console.log('[EmojiPickerModal] onEmoji 调用完成');
    // 点击表情后不关闭弹窗，让用户可以继续选择
  };

  if (!visible) {
    return null;
  }

  // 计算气泡位置，靠左显示（固定位置，避免闪烁）
  const bubbleWidth = 320;
  const inputAreaHeight = 200; // 输入区域高度
  const spaceFromInput = 8; // 距离输入框的间距
  
  const bubbleStyle = {
    width: bubbleWidth,
    bottom: inputAreaHeight + spaceFromInput, // 在输入框上方
    left: 16, // 靠左显示
  };

  console.log('[EmojiPickerModal] 渲染, visible:', visible, 'bubbleStyle:', bubbleStyle);
  
  // 暂时移除遮罩层，避免拦截点击事件
  // 点击外部关闭的功能由 ChatInput 中的 handleClickOutside 处理
  
  return (
    <View 
      style={[styles.bubble, bubbleStyle]}
      data-emoji-picker="true"
      onStartShouldSetResponder={() => {
        console.log('[EmojiPickerModal] 气泡 onStartShouldSetResponder');
        return true;
      }}
      onMoveShouldSetResponder={() => {
        console.log('[EmojiPickerModal] 气泡 onMoveShouldSetResponder');
        return true;
      }}
      onResponderGrant={() => {
        console.log('[EmojiPickerModal] 气泡 onResponderGrant');
      }}
    >
        <View style={styles.content}>
          <EmojiGrid 
            screenWidth={bubbleStyle.width || screenWidth - 32}
            onEmoji={handleEmojiPress}
          />
        </View>
    </View>
  );
};

const EmojiGrid = ({ screenWidth, onEmoji }: { 
  screenWidth: number; 
  onEmoji: (emoji: string) => void;
}) => {
  const spanCount = 8;
  const textSize = isIos ? 34 : 30;
  const horizontalPadding = 12;
  // screenWidth 可能是气泡的实际宽度
  const availableWidth = screenWidth - (horizontalPadding * 2);
  const width = availableWidth / spanCount;
  const itemHeight = 52;
  const containerRef = useRef<View>(null);

  // 在 Web 上，添加原生 DOM 事件监听器作为备用
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      console.log('[EmojiGrid] containerRef.current 为空');
      return;
    }

    console.log('[EmojiGrid] 开始设置 DOM 事件监听器, container:', container);

    const handleClick = (e: any) => {
      console.log('[EmojiGrid] DOM click 事件触发, target:', e.target);
      const target = e.target;
      if (!target) return;

      // 查找包含表情文本的元素
      let currentElement: any = target;
      let depth = 0;
      const maxDepth = 10;

      while (currentElement && depth < maxDepth) {
        // 检查是否是文本节点或包含表情的节点
        const textContent = currentElement.textContent || currentElement.innerText || '';
        const trimmedText = textContent.trim();
        
        if (trimmedText && emojiArr.includes(trimmedText)) {
          const emoji = trimmedText;
          console.log('[EmojiGrid] DOM click 事件（备用）, emoji:', emoji);
          if (emoji) {
            e.stopPropagation();
            e.preventDefault();
            onEmoji(emoji);
            return;
          }
        }
        currentElement = currentElement.parentElement;
        depth++;
      }
      
      console.log('[EmojiGrid] DOM click 事件未找到表情');
    };

    // 尝试获取原生 DOM 节点
    try {
      const containerEl = container as any;
      let domNode: any = null;

      console.log('[EmojiGrid] 尝试获取 DOM 节点, containerEl:', containerEl);

      if (containerEl._internalFiberInstanceHandleDEV?.stateNode?.node) {
        domNode = containerEl._internalFiberInstanceHandleDEV.stateNode.node;
        console.log('[EmojiGrid] 通过方法1找到DOM节点');
      } else if (containerEl._nativeNode) {
        domNode = containerEl._nativeNode;
        console.log('[EmojiGrid] 通过方法2找到DOM节点');
      } else {
        // 方法3: 通过 document 查找
        const doc: any = (global as any).document;
        if (doc) {
          // 查找包含 data-emoji-picker-scroll 的元素
          const found = doc.querySelector('[data-emoji-picker-scroll]');
          if (found) {
            domNode = found;
            console.log('[EmojiGrid] 通过方法3找到DOM节点');
          }
        }
      }

      if (domNode) {
        console.log('[EmojiGrid] 成功找到DOM节点，添加 click 监听器');
        domNode.addEventListener('click', handleClick, true);
        // 也添加 mousedown 事件
        domNode.addEventListener('mousedown', handleClick, true);
        return () => {
          console.log('[EmojiGrid] 移除 DOM 事件监听器');
          domNode.removeEventListener('click', handleClick, true);
          domNode.removeEventListener('mousedown', handleClick, true);
        };
      } else {
        console.log('[EmojiGrid] 未找到DOM节点');
      }
    } catch (e) {
      console.log('[EmojiGrid] 无法添加 DOM 监听器, 错误:', e);
    }
  }, [onEmoji]);

  // 将 renderItem 改为独立组件，以便使用 hooks
  const EmojiItem = React.memo(({ item }: { item: string }) => {
    const itemRef = useRef<View>(null);
    const [isPressed, setIsPressed] = useState(false);
    
    // 为每个 item 添加原生 DOM 事件监听器
    useEffect(() => {
      const view = itemRef.current;
      if (!view) {
        console.log('[EmojiItem] itemRef.current 为空, emoji:', item);
        return;
      }

      const handleClick = (e: any) => {
        console.log('[EmojiItem] DOM click 事件, emoji:', item, 'target:', e.target);
        e.stopPropagation();
        e.preventDefault();
        onEmoji(item);
      };

      // 延迟获取 DOM 节点，确保已经渲染
      const timeoutId = setTimeout(() => {
        try {
          const viewEl = view as any;
          let domNode: any = null;

          if (viewEl._internalFiberInstanceHandleDEV?.stateNode?.node) {
            domNode = viewEl._internalFiberInstanceHandleDEV.stateNode.node;
            console.log('[EmojiItem] 通过方法1找到DOM节点, emoji:', item);
          } else if (viewEl._nativeNode) {
            domNode = viewEl._nativeNode;
            console.log('[EmojiItem] 通过方法2找到DOM节点, emoji:', item);
          } else if (viewEl.nodeName) {
            // 如果已经是 DOM 元素
            domNode = viewEl;
            console.log('[EmojiItem] 通过方法3找到DOM节点, emoji:', item);
          }

          if (domNode) {
            console.log('[EmojiItem] 成功找到DOM节点，添加事件监听器, emoji:', item);
            domNode.addEventListener('click', handleClick, true);
            domNode.addEventListener('mousedown', handleClick, true);
          } else {
            console.log('[EmojiItem] 未找到DOM节点, emoji:', item, 'viewEl:', viewEl);
          }
        } catch (e) {
          console.log('[EmojiItem] 添加事件监听器失败, emoji:', item, '错误:', e);
        }
      }, 100);

      return () => {
        clearTimeout(timeoutId);
      };
    }, [item, onEmoji]);

    return (
      <View
        ref={itemRef}
        style={{
          alignItems: "center",
          justifyContent: "center",
          width: width,
          height: itemHeight,
          cursor: 'pointer',
        }}
        onStartShouldSetResponder={() => {
          console.log('[EmojiItem] onStartShouldSetResponder, emoji:', item);
          return true;
        }}
        onResponderGrant={() => {
          console.log('[EmojiItem] onResponderGrant, emoji:', item);
          setIsPressed(true);
          onEmoji(item);
          // 延迟重置按下状态，提供视觉反馈
          setTimeout(() => setIsPressed(false), 200);
        }}
      >
        <TouchableOpacity
          activeOpacity={0.7}
          onPress={() => {
            console.log('[EmojiItem] TouchableOpacity onPress 被调用, emoji:', item);
            setIsPressed(true);
            onEmoji(item);
            setTimeout(() => setIsPressed(false), 200);
          }}
          onPressIn={() => setIsPressed(true)}
          onPressOut={() => setTimeout(() => setIsPressed(false), 150)}
          style={{
            alignItems: "center",
            justifyContent: "center",
            width: '100%',
            height: '100%',
            borderRadius: 8,
            backgroundColor: isPressed ? ColorF5F5F5 : 'transparent',
          }}
        >
          <Text 
            style={{ 
              fontSize: textSize, 
              color: black,
              textAlign: 'center',
            }}
          >
            {item}
          </Text>
        </TouchableOpacity>
      </View>
    );
  });

  function renderItem({ item }: { item: string }) {
    return <EmojiItem item={item} />;
  }

  // 在 Web 上，为容器添加滚动事件监听，阻止冒泡
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = (e: any) => {
      // 阻止滚动事件冒泡到 window
      if (e.stopPropagation) {
        e.stopPropagation();
      }
      console.log('[EmojiGrid] 容器滚动事件，已阻止冒泡');
    };

    try {
      const containerEl = container as any;
      let domNode: any = null;

      if (containerEl._internalFiberInstanceHandleDEV?.stateNode?.node) {
        domNode = containerEl._internalFiberInstanceHandleDEV.stateNode.node;
      } else if (containerEl._nativeNode) {
        domNode = containerEl._nativeNode;
      }

      if (domNode) {
        console.log('[EmojiGrid] 为容器添加滚动事件监听（阻止冒泡）');
        domNode.addEventListener('scroll', handleScroll, true);
        return () => {
          domNode.removeEventListener('scroll', handleScroll, true);
        };
      }
    } catch (e) {
      console.log('[EmojiGrid] 无法添加滚动事件监听:', e);
    }
  }, []);

  return (
    <View 
      ref={containerRef}
      style={styles.emojiContainer} 
      data-emoji-picker-scroll="true"
    >
      <FlatList
        data={emojiArr}
        renderItem={renderItem}
        numColumns={spanCount}
        keyExtractor={(item, index) => index.toString()}
        contentContainerStyle={[styles.emojiListContent, { paddingHorizontal: horizontalPadding }]}
        showsVerticalScrollIndicator={true}
        style={{ maxHeight: 320 }}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0, // 覆盖整个屏幕
    backgroundColor: 'rgba(0, 0, 0, 0.01)', // 几乎透明，但可以接收点击
    zIndex: 1000,
    pointerEvents: 'auto', // 确保可以点击关闭
  },
  bubble: {
    position: 'absolute',
    backgroundColor: white,
    borderRadius: 16,
    maxHeight: 380,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 12,
    elevation: 8,
    overflow: 'hidden',
    zIndex: 1002, // 确保气泡在遮罩层之上
    pointerEvents: 'auto', // 确保可以接收事件
  },
  content: {
    minHeight: 220,
    maxHeight: 380,
    paddingBottom: 12,
    pointerEvents: 'auto', // 确保内容区域可以接收点击事件
  },
  emojiContainer: {
    flex: 1,
  },
  emojiListContent: {
    paddingVertical: 12,
    paddingBottom: 20,
  },
});
